import {testIdProps} from '@github-ui/test-id-props'
import {AlertFillIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, AnchoredOverlay} from '@primer/react'
import {memo, useCallback, useEffect, useMemo, useRef} from 'react'
import {flushSync} from 'react-dom'

import {MemexColumnDataType} from '../api/columns/contracts/memex-column'
import {onSubMenuMultiSelection} from '../helpers/util'
import {ViewerPrivileges} from '../helpers/viewer-privileges'
import {useColumnWarnings} from '../hooks/use-column-warning'
import {useMemexRootHeight} from '../hooks/use-memex-root-height'
import {useViewOptionsStatsUiKey} from '../hooks/use-view-options-stats-ui-key'
import {useViews} from '../hooks/use-views'
import {useVisibleFields} from '../hooks/use-visible-fields'
import type {ColumnModel} from '../models/column-model'
import {useAllColumns} from '../state-providers/columns/use-all-columns'
import {useIssueFields} from '../state-providers/issue-fields/use-issue-fields'
import {useAddFieldModal} from '../state-providers/modals/use-add-field-modal'
import {useUserNotices} from '../state-providers/user-notices/user-notices-provider'
import {IssueFieldResources} from '../strings'
import styles from './add-column-menu-v2.module.css'
import {getColumnIcon} from './column-detail-helpers'
import {normalizeToFilterName} from './filter-bar/helpers/search-filter'
import {IssueFieldsQuickAdd} from './issue-fields-quick-add'

const FieldMenuGroups = {
  NewFieldGroup: 'NewFieldGroup',
  DefaultGroup: 'DefaultGroup',
  ProjectFieldGroup: 'ProjectFieldGroup',
} as const
type FieldMenuGroups = ObjectValues<typeof FieldMenuGroups>

type MenuProps = {
  id?: string
  open: boolean
  setOpen: (open: boolean) => void
  anchorRef: React.RefObject<HTMLElement>
}

export const AddColumnMenuV2 = memo<MenuProps>(function AddColumnMenu({id, open, setOpen, anchorRef}) {
  const {showAddFieldModal, setShowAddFieldModal} = useAddFieldModal()
  const {loadIssueFieldSuggestionsIfNeeded} = useIssueFields()
  const {clientHeight} = useMemexRootHeight({
    onResize: () => {
      if (open) {
        // eslint-disable-next-line @eslint-react/dom/no-flush-sync
        flushSync(() => {
          setOpen(false)
        })

        setOpen(true)
      }
    },
  })

  const actionListRef = useRef<HTMLUListElement>(null)

  const {userNotices, dismissUserNotice} = useUserNotices()
  const {hasWritePermissions} = ViewerPrivileges()

  const closeOverlayIfNoSubMenuDialog = () => {
    if (!showAddFieldModal) {
      setOpen(false)
    }
  }

  const dismissIssueFieldNotice = useCallback(() => {
    dismissUserNotice('project_issue_fields_onboarding')
  }, [dismissUserNotice])

  useEffect(() => {
    if (open) {
      loadIssueFieldSuggestionsIfNeeded()
    }
  }, [loadIssueFieldSuggestionsIfNeeded, open])

  return (
    <AnchoredOverlay
      anchorRef={anchorRef}
      renderAnchor={null}
      open={open}
      focusZoneSettings={{containerRef: actionListRef}}
      overlayProps={{
        style: {maxHeight: clientHeight},
        className: styles.ActionMenu_Overlay,
        onEscape: closeOverlayIfNoSubMenuDialog,
        onClickOutside: closeOverlayIfNoSubMenuDialog,
        anchorSide: 'outside-right',
        ...testIdProps('column-visibility-menu'),
      }}
    >
      {userNotices.project_issue_fields_onboarding && hasWritePermissions && (
        <IssueFieldsQuickAdd onSeeMore={() => setShowAddFieldModal(true)} onClose={dismissIssueFieldNotice} />
      )}
      <ActionList ref={actionListRef}>
        <Options id={id} key={String(open)} />
      </ActionList>
    </AnchoredOverlay>
  )
})

const Options = memo(function Options({id}: Pick<MenuProps, 'id'>) {
  const {currentView} = useViews()
  const {showAddFieldModal, setShowAddFieldModal} = useAddFieldModal()

  const {allColumns} = useAllColumns()
  const {isFieldVisible, toggleField} = useVisibleFields()

  const columnWarnings = useColumnWarnings(allColumns)

  const {hasWritePermissions} = ViewerPrivileges()
  const statsUiKey = useViewOptionsStatsUiKey()

  const onClickNewField = useCallback(
    (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      setShowAddFieldModal(true)
      event.stopPropagation()
    },
    [setShowAddFieldModal],
  )

  const getItem = useCallback(
    (column: ColumnModel, groupType: FieldMenuGroups) => {
      const warningKind = columnWarnings.get(column.id)
      const showColumnWarning = warningKind === 'column-name-collides-with-issue-field'

      const isVisible = isFieldVisible(column)
      const isDisabled = isVisibleFieldDisabled(column)
      const Icon = getColumnIcon(column.dataType)

      const warningMessage = showColumnWarning ? IssueFieldResources.fieldNameCollisionText : column.name

      return (
        <ActionList.Item
          key={column.id}
          groupId={groupType}
          disabled={isDisabled}
          role="option"
          aria-label={warningMessage}
          onSelect={
            !isDisabled
              ? event => {
                  onSubMenuMultiSelection(event)
                  // the column is currently visible, so the stat is that we are hiding it.
                  if (!currentView) return

                  toggleField(currentView.number, column, undefined, statsUiKey)
                  event.stopPropagation()
                }
              : undefined
          }
          selected={isVisible}
          {...testIdProps(columnItemTestId(isVisible, column.name))}
        >
          <ActionList.LeadingVisual>
            <Icon />
          </ActionList.LeadingVisual>
          {column.name}
          {showColumnWarning && (
            <ActionList.TrailingVisual
              className={styles.warningIcon}
              {...testIdProps(`column-warning-icon-${column.name}`)}
            >
              <AlertFillIcon size={12} />
              <span className={styles.warningText}>{IssueFieldResources.fieldNameCollisionText}</span>
            </ActionList.TrailingVisual>
          )}
        </ActionList.Item>
      )
    },
    [columnWarnings, isFieldVisible, currentView, toggleField, statsUiKey],
  )

  const [nonProjectColumns, projectColumns] = useMemo(() => {
    return allColumns.reduce<[Array<React.ReactElement>, Array<React.ReactElement>]>(
      (acc, col) => {
        if (col.isProjectField) {
          acc[1].push(getItem(col, FieldMenuGroups.ProjectFieldGroup))
        } else {
          acc[0].push(getItem(col, FieldMenuGroups.DefaultGroup))
        }
        return acc
      },
      [[], []],
    )
  }, [allColumns, getItem])

  const addColumnAllowed = useMemo(() => {
    // "New field" option should be enabled if a user has write permissions, the Modal for new field is not shown and it is a table view
    return hasWritePermissions && !showAddFieldModal
  }, [hasWritePermissions, showAddFieldModal])

  return (
    <ActionList id={id} className={styles.ActionList} role="listbox" {...testIdProps('visible-columns-menu')}>
      <ActionList.Group>
        <ActionList.Item
          key={'add-field'}
          disabled={!addColumnAllowed}
          onSelect={e => {
            onSubMenuMultiSelection(e)
            onClickNewField(e)
          }}
          {...testIdProps('add-field-button')}
        >
          <ActionList.LeadingVisual>
            <PlusIcon />
          </ActionList.LeadingVisual>
          Add field
        </ActionList.Item>
      </ActionList.Group>
      <ActionList.Divider />
      <ActionList.Group {...testIdProps('visible-columns-menu-default-group')} selectionVariant="multiple">
        {nonProjectColumns}
      </ActionList.Group>
      {projectColumns.length > 0 && (
        <ActionList.Group {...testIdProps('visible-columns-menu-project-group')} selectionVariant="multiple">
          <ActionList.GroupHeading>Project fields</ActionList.GroupHeading>
          {projectColumns}
        </ActionList.Group>
      )}
    </ActionList>
  )
})

// Returns true if a field/column should be disabled in the actions menu
function isVisibleFieldDisabled(column: ColumnModel) {
  // Title column is always visible and can't be hidden
  if (column.dataType === MemexColumnDataType.Title) {
    return true
  }

  return false
}

// Returns the test ID of a column item in the actions menu, based on whether it's visible or hidden
function columnItemTestId(isFieldVisible: boolean, columnName: string) {
  const kebabCaseColumnName = normalizeToFilterName(columnName)

  return `${isFieldVisible ? 'visible' : 'hidden'}-column-item-${kebabCaseColumnName}`
}

try{ AddColumnMenuV2.displayName ||= 'AddColumnMenuV2' } catch {}
try{ Options.displayName ||= 'Options' } catch {}
try{ Icon.displayName ||= 'Icon' } catch {}